import { DURATION_SECONDS, VASO_POINTS } from "@clubap/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { finishGame, startGame, type GameResult } from "../api.js";
import { FrozenStage, type FrozenAction, type FrozenHud } from "../game/FrozenStage.js";

interface Props {
  /** Índice en `AVATAR_COLORS`. El juego dibuja TU avatar, no un muñeco genérico. */
  color: number;
  /** Lo que llevas puesto: saltas con tu gorra. */
  equipped: readonly string[];
  onFinished: (result: GameResult) => void;
  onClose: () => void;
}

type Phase = "idle" | "playing" | "sending" | "done";

const EMPTY_HUD: FrozenHud = {
  score: 0,
  remaining: DURATION_SECONDS,
  chain: 0,
  vasos: 0,
  stunned: false,
};

/**
 * El puesto del carrito de la FIEC Vieja.
 *
 * Este archivo NO tiene juego adentro: el bucle vive en `FrozenStage`, que es dueño del
 * canvas. Acá quedan el marco, los botones y las dos llamadas HTTP, que es el reparto
 * que impide que sesenta fotogramas por segundo se conviertan en sesenta renders de
 * React por segundo.
 *
 * La partida la abre y la cierra el SERVIDOR: `startGame` devuelve la semilla de la que
 * sale todo el desfile de frutas, y `finishGame` es lo único que puede acreditar. Acá no
 * se calcula una sola moneda.
 */
export function FrozenGame({ color, equipped, onFinished, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [hud, setHud] = useState<FrozenHud>(EMPTY_HUD);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GameResult | null>(null);

  const hostRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<FrozenStage | null>(null);
  const sessionRef = useRef<string | null>(null);
  /** El puntaje final lo dicta el escenario; el estado es para pintarlo. */
  const finalScore = useRef(0);

  /*
   * El aspecto con el que se construye el avatar, en una ref y no en las dependencias.
   *
   * `equipped` es un array nuevo en cada render del padre, así que ponerlo como
   * dependencia reconstruiría el canvas —y mataría la partida en curso— cada vez que
   * cambiara cualquier otra cosa de la cuenta. Es la misma trampa que en este repo dejó
   * la tienda cerrándose sola: depender del objeto y no de su identidad.
   */
  const look = useRef({ color, equipped });
  look.current = { color, equipped };

  /* El escenario se monta UNA vez y se reutiliza en cada partida. Sin dependencias a
     propósito: nada de lo que este panel recibe justifica tirar el canvas. */
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let cancelled = false;
    let mounted: FrozenStage | null = null;

    void (async () => {
      try {
        const stage = await FrozenStage.create(host, {
          color: look.current.color,
          equipped: look.current.equipped,
          onHud: setHud,
          onEnded: (score) => {
            finalScore.current = score;
            setPhase("sending");
          },
        });

        if (cancelled) {
          stage.destroy();
          return;
        }

        mounted = stage;
        stageRef.current = stage;
        setReady(true);
      } catch {
        setError("No se pudo abrir el puesto.");
      }
    })();

    return () => {
      cancelled = true;
      mounted?.destroy();
      stageRef.current = null;
    };
  }, []);

  const begin = useCallback(async () => {
    setError(null);
    setResult(null);
    setHud(EMPTY_HUD);

    try {
      const started = await startGame("frozen");
      sessionRef.current = started.sessionId;
      stageRef.current?.start(started.seed);
      setPhase("playing");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo empezar.");
    }
  }, []);

  // El envío va en su propio efecto: cerrarla desde dentro del bucle del canvas mezclaría
  // una petición de red con el fotograma que la disparó.
  useEffect(() => {
    if (phase !== "sending") return;

    const sessionId = sessionRef.current;
    if (!sessionId) return;

    void (async () => {
      try {
        const finished = await finishGame(sessionId, finalScore.current);
        setResult(finished);
        onFinished(finished);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "No se pudo cerrar la partida.");
      } finally {
        setPhase("done");
      }
    })();
  }, [phase, onFinished]);

  /* Los botones mandan la misma intención que el teclado. El escenario ya escucha las
     teclas por su cuenta; esto es para el dedo, que no tiene ninguna. */
  const hold = (action: FrozenAction) => ({
    onPointerDown: (event: React.PointerEvent) => {
      event.preventDefault();
      stageRef.current?.press(action, true);
    },
    onPointerUp: () => stageRef.current?.press(action, false),
    onPointerLeave: () => stageRef.current?.press(action, false),
    onPointerCancel: () => stageRef.current?.press(action, false),
  });

  return (
    <div className="overlay">
      <div className="panel frozen">
        <div className="game__head">
          <h2 className="game__title">Frozen de Frutas</h2>
          {phase === "playing" && <span className="game__timer">{hud.remaining}s</span>}
          <span className="game__score">◉ {hud.score}</span>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar juego">
            ×
          </button>
        </div>

        <div className="frozen__court">
          <div className="frozen__canvas" ref={hostRef} />

          {/* La capa de mensajes va ENCIMA del canvas, no en lugar de él: desmontarlo
              entre partidas tiraría el escenario y volvería a cargar el arte. */}
          {phase !== "playing" && (
            <div className="frozen__curtain">
              {phase === "idle" && (
                <>
                  <p className="game__hint">
                    La fruta pasa de derecha a izquierda: salta y cáele encima para romperla.
                    Sin tocar el suelo, cada una vale más. <strong>El ají no.</strong>
                  </p>
                  <button
                    className="btn"
                    type="button"
                    disabled={!ready}
                    onClick={() => void begin()}
                  >
                    {ready ? "Empezar" : "Abriendo…"}
                  </button>
                </>
              )}

              {phase === "sending" && <p className="game__hint">Enviando…</p>}

              {phase === "done" && (
                <>
                  <p className="game__result">
                    {hud.vasos === 1 ? "1 vaso servido" : `${hud.vasos} vasos servidos`}
                  </p>
                  {result && (
                    <>
                      <p className="game__result">+{result.coinsAwarded} monedas</p>
                      {result.cappedByLimit && (
                        <p className="game__hint">Alcanzaste el techo por hora.</p>
                      )}
                    </>
                  )}
                  <button className="btn" type="button" onClick={() => void begin()}>
                    Otra vez
                  </button>
                </>
              )}
            </div>
          )}

          {/* La cadena viva. Sólo desde la segunda, que es cuando empieza a significar algo. */}
          {phase === "playing" && hud.chain > 1 && (
            <span className="frozen__chain">×{hud.chain}</span>
          )}

          {phase === "playing" && hud.stunned && (
            <span className="frozen__stun">¡Ají!</span>
          )}
        </div>

        <div className="frozen__pad">
          <button
            className="frozen__key"
            type="button"
            aria-label="Izquierda"
            {...hold("izquierda")}
          >
            ←
          </button>
          <button
            className="frozen__key frozen__key--jump"
            type="button"
            aria-label="Saltar"
            {...hold("salto")}
          >
            SALTAR
          </button>
          <button
            className="frozen__key"
            type="button"
            aria-label="Derecha"
            {...hold("derecha")}
          >
            →
          </button>
        </div>

        <p className="frozen__keys">
          <strong>← →</strong> moverte · <strong>espacio</strong> saltar · cada{" "}
          {VASO_POINTS} de puré es un vaso
        </p>

        {error && (
          <p className="game__error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
